import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { ThemeContext } from '../../context/ThemeContext';
import { useMetadata } from '../../context/MetadataContext';
import { useCreatedPuzzles } from '../../hooks/useCreatedPuzzles';
import { formatDateUppercase } from '../../utils/formatDate';
import CreatedPuzzleDetail from '../CreatedPuzzleDetail';
import styles from './CreatedPuzzles.module.css';

/**
 * CreatedPuzzles component for displaying a list of user's created puzzles
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 */
const CreatedPuzzles = ({ isOpen, onClose }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const { getText } = useMetadata();
  const { pendingPuzzles, privatePuzzles, loading, error } = useCreatedPuzzles(isOpen);
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);

  // Add event listener to close modal on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restore scrolling when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking outside of content
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRowClick = (puzzle) => {
    setSelectedPuzzle(puzzle);
  };

  const handleDetailClose = () => {
    setSelectedPuzzle(null);
  };


  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={`${styles.modalContent} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {getText('create.created_title')}
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {loading && (
            <div className={styles.loadingMessage}>
              {getText('common_text.loading')}
            </div>
          )}
          
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
          
          {!loading && !error && pendingPuzzles.length === 0 && privatePuzzles.length === 0 && (
            <div className={styles.emptyMessage}>
              {getText('create.no_puzzles')}
            </div>
          )}
          
          {/* Show sections when there are any puzzles */}
          {!loading && !error && (pendingPuzzles.length > 0 || privatePuzzles.length > 0) && (
            <>
              {/* Pending Puzzles Section */}
              <div className={styles.puzzleSection}>
                <h3 className={styles.sectionTitle}>{getText('create.pending_title')}</h3>
                {pendingPuzzles.length > 0 ? (
                  <div className="puzzleTableContainer">
                    <table className="puzzleTable">
                      <thead>
                        <tr>
                          <th>{getText('common_text.style')}</th>
                          <th>{getText('common_text.size')}</th>
                          <th>{getText('common_text.submitted')}</th>
                          <th>{getText('common_text.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="puzzleTableBody">
                        {pendingPuzzles.map((puzzle, index) => (
                          <tr 
                            key={`pending-${index}`} 
                            className={styles.clickableRow}
                            onClick={() => handleRowClick(puzzle)}
                          >
                            <td className="puzzleStyleCell">
                              {puzzle.style.charAt(0).toUpperCase() + puzzle.style.slice(1)}
                            </td>
                            <td className="puzzleSizeCell">
                              {puzzle.size}x{puzzle.size}
                            </td>
                            <td className="puzzleDateCell">
                              {formatDateUppercase(puzzle.submitted_ts || puzzle.created_ts)}
                            </td>
                            <td>
                              {puzzle.status ? puzzle.status.charAt(0).toUpperCase() + puzzle.status.slice(1) : 'Pending'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={styles.emptyMessage}>
                    There are no pending puzzles
                  </div>
                )}
              </div>

              {/* Recently Created Puzzles Section */}
              <div className={styles.puzzleSection}>
                <h3 className={styles.sectionTitle}>{getText('create.accepted_title')}</h3>
                {privatePuzzles.length > 0 ? (
                  <div className="puzzleTableContainer">
                    <table className="puzzleTable">
                      <thead>
                        <tr>
                          <th>{getText('common_text.style')}</th>
                          <th>{getText('common_text.size')}</th>
                          <th>{getText('common_text.accepted')}</th>
                          <th>{getText('common_text.difficulty') }</th>
                        </tr>
                      </thead>
                      <tbody className="puzzleTableBody">
                        {privatePuzzles.map((puzzle, index) => (
                          <tr 
                            key={`private-${index}`} 
                            className={styles.clickableRow}
                            onClick={() => handleRowClick(puzzle)}
                          >
                            <td className="puzzleStyleCell">
                              {puzzle.style.charAt(0).toUpperCase() + puzzle.style.slice(1)}
                            </td>
                            <td className="puzzleSizeCell">
                              {puzzle.size}x{puzzle.size}
                            </td>
                            <td className="puzzleDateCell">
                              {formatDateUppercase(puzzle.accepted_ts || puzzle.created_ts)}
                            </td>
                            <td>
                              {puzzle.difficulty_estimate || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={styles.emptyMessage}>
                    There are no recently created puzzles
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Render CreatedPuzzleDetail modal if a puzzle is selected */}
      {selectedPuzzle && (
        <div className={styles.detailModalBackdrop}>
          <div className={`${styles.detailModalContent} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
            <div className={styles.detailModalHeader}>
              <button 
                className={styles.detailCloseButton} 
                onClick={handleDetailClose}
                aria-label="Close detail modal"
              >
                <X size={20} />
              </button>
            </div>
            <CreatedPuzzleDetail 
              puzzleData={selectedPuzzle} 
              onClose={handleDetailClose}
            />
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default CreatedPuzzles;